import React, { useEffect, useMemo, useRef } from 'react';
import { File as FileIcon, Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { compressImage } from '../../../utils/upload';

/** Pages of one sheet per parse — the upload endpoint accepts at most this many. */
export const MAX_SHEET_FILES = 4;

interface Props {
    files: File[];
    onChange: (files: File[]) => void;
}

/**
 * Photo(s)/PDF picker for a filled-in paper sheet (STOCK.md §2.5), shared by the
 * order forms (attach up front, before Generate) and Generate Order itself (attach
 * at parse time). compressImage downscales photos for the model (§12.1 sizing) and
 * passes PDFs through unchanged; a file it can't read goes up as-is.
 */
export const SheetFilePicker: React.FC<Props> = ({ files, onChange }) => {
    const fileInput = useRef<HTMLInputElement | null>(null);

    // Object URLs for the image thumbnails — revoked when the list changes or the
    // component unmounts so they don't leak.
    const previews = useMemo(
        () => files.map((f) => (f.type.startsWith('image/') ? URL.createObjectURL(f) : null)),
        [files],
    );
    useEffect(() => () => previews.forEach((u) => u && URL.revokeObjectURL(u)), [previews]);

    const handleFiles = async (picked: FileList | null) => {
        if (!picked?.length) return;
        const compressed = await Promise.all(
            Array.from(picked).map((f) => compressImage(f, 1600, 1600).catch(() => f)));
        if (files.length + compressed.length > MAX_SHEET_FILES) {
            toast.error(`Up to ${MAX_SHEET_FILES} files at a time — the extra one(s) were ignored.`);
        }
        onChange([...files, ...compressed].slice(0, MAX_SHEET_FILES));
    };

    return (
        <div>
            <input
                ref={fileInput}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif,application/pdf"
                multiple
                className="hidden"
                onChange={(e) => {
                    handleFiles(e.target.files);
                    // Reset so picking the same file again still fires onChange.
                    e.target.value = '';
                }}
            />
            <button
                type="button"
                onClick={() => fileInput.current?.click()}
                className="w-full rounded-lg border-2 border-dashed border-border bg-white px-4 py-4 text-sm text-muted-foreground hover:bg-accent/40 flex flex-col items-center gap-2"
            >
                <Upload size={20} />
                Add photos or a PDF of the sheet
            </button>
            <p className="text-xs text-muted-foreground mt-2">
                Up to {MAX_SHEET_FILES} files — several photos are read as pages of one sheet.
            </p>

            {files.length > 0 && (
                <div className="divide-y divide-border border border-border rounded-lg mt-2 bg-white">
                    {files.map((f, index) => {
                        const preview = previews[index];
                        return (
                            <div key={`${f.name}-${index}`} className="flex items-center gap-3 p-2">
                                {preview ? (
                                    <img src={preview} alt={f.name}
                                         className="w-10 h-10 object-cover rounded-md border border-border shrink-0" />
                                ) : (
                                    <span className="w-10 h-10 rounded-md border border-border bg-muted/40 flex items-center justify-center shrink-0">
                                        <FileIcon size={16} className="text-muted-foreground" />
                                    </span>
                                )}
                                <span className="text-sm truncate flex-1">{f.name}</span>
                                <Button type="button" variant="ghost" size="icon" aria-label="Remove file"
                                        onClick={() => onChange(files.filter((_, i) => i !== index))}>
                                    <X size={16} />
                                </Button>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default SheetFilePicker;
